import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import './ModalStyle.scss'
import { TextField, MenuItem } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Autocomplete from '@mui/material/Autocomplete'
import { AutocompleteTextField } from './CustomerStyledComponents'
import { useLazyQuery } from '@apollo/client'
import { gqlAddressAutoFill } from '../../gql'

function CheckboxSelectSite({
  availableSites,
  availableSitesBdn,
  onChange,
  label,
  siteKey,
  orderDelivery,
  isNotModifiable,
}) {
  const { t } = useTranslation('order_detail')
  const [searchAddresses] = useLazyQuery(gqlAddressAutoFill.SEARCH_ADDRESSES, { fetchPolicy: 'network-only' })
  const [searchCities] = useLazyQuery(gqlAddressAutoFill.SEARCH_CITIES, { fetchPolicy: 'network-only' })
  const [addressOptions, setAddressOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const addressTimerRef = useRef(null)
  const cityTimerRef = useRef(null)

  const getInitialData = (key) => {
    const getData = (source) => ({
      isDepot: source?.isSite || false,
      id: source?.id || '',
      address: source?.address || '',
      city: source?.city || '',
      code: source?.code || '',
    })

    if (orderDelivery[key + 'Bdn']?.isSite) {
      return getData(orderDelivery[key + 'Bdn'])
    } else if (orderDelivery[key]) {
      return getData(orderDelivery[key])
    } else {
      return {
        isDepot: false,
        id: '',
        address: orderDelivery[`${key}Address`] || '',
        city: orderDelivery[`${key}City`] || '',
        code: '',
      }
    }
  }

  const { isDepot, id, address, city, code } = getInitialData(siteKey)

  const [isDepotState, setIsDepot] = useState(isDepot || false)
  const [idState, setId] = useState(id)
  const [addressState, setAddress] = useState(address)
  const [cityState, setCity] = useState(city)
  const [codeState, setCode] = useState(code)

  useEffect(() => {
    setIsDepot(isDepot || false)
    setId(id)
    setAddress(address)
    setCity(city)
    setCode(code)
  }, [isDepot, id, address, city, code, siteKey])

  const handleChange = (newIsDepot, newSite, newAddress, newCity, newCode) => {
    onChange(
      {
        isDepot: newIsDepot,
        id: newSite,
        address: newAddress,
        city: newCity,
        code: newCode,
      },
      siteKey
    )
  }

  const onChangeDepot = (e) => {
    const newIsDepot = e.target.checked
    setId('')
    setAddress('')
    setCity('')
    setCode('')
    handleChange(newIsDepot, '', '', '', '')
    setIsDepot(newIsDepot)
  }

  const onSiteChange = (e) => {
    const newSite = e.target.value
    setId(newSite)
    handleChange(isDepotState, newSite, addressState, cityState, codeState)
  }

  const onAddressChange = (e) => {
    const newAddress = e.target.value
    setAddress(newAddress)
    handleChange(isDepotState, idState, newAddress, cityState, codeState)
  }

  const onCityChange = (e) => {
    const newCity = e.target.value
    setCity(newCity)
    handleChange(isDepotState, idState, addressState, newCity, codeState)
  }

  const handleSiteCodeChange = (event, newValue) => {
    if (!newValue) return
    setAddress(newValue.address)
    setCity(newValue.city)
    setId(newValue.id)
    setCode(newValue.code)
    handleChange(isDepotState, newValue.id, newValue.address, newValue.city, newValue.code)
  }

  const handleSiteCodeInputChange = (event, newInputValue, reason) => {
    if (reason === 'reset') return
    const matchedSiteCode = availableSites?.find((siteCode) => siteCode.code === newInputValue)
    setAddress(matchedSiteCode?.address || '')
    setCity(matchedSiteCode?.city || '')
    setId(matchedSiteCode?.id || '')
    setCode(matchedSiteCode?.code || '')
    handleChange(
      isDepotState,
      matchedSiteCode?.id || '',
      matchedSiteCode?.address || '',
      matchedSiteCode?.city || '',
      matchedSiteCode?.code || ''
    )
  }

  const parseAddress = (address) => {
    if (!address) return { number: '', street: '' }
    const match = address.match(/^(\d+(?:\s*(?:bis|ter|quater|quinquies))?)\s+(.+)$/i)
    if (match) {
      return {
        number: match[1].trim(),
        street: match[2].trim(),
      }
    }
    return {
      number: '',
      street: address.trim(),
    }
  }

  const formatOption = (option, inputValue) => {
    const { number: inputNumber } = parseAddress(inputValue)
    if (inputNumber) {
      return `${inputNumber} ${option}`
    }
    return option
  }

  const customFilterOptions = (options, { inputValue }) => {
    const { street: inputStreet } = parseAddress(inputValue)
    if (!inputStreet) return options
    const lowerInput = inputStreet.toLowerCase()
    return options.filter((option) => {
      const { street: optionStreet } = parseAddress(option)
      return optionStreet.toLowerCase().includes(lowerInput)
    })
  }

  const handleAddressSearch = useCallback(
    (value) => {
      if (addressTimerRef.current) clearTimeout(addressTimerRef.current)
      if (!value || value.length < 2) {
        setAddressOptions([])
        return
      }
      addressTimerRef.current = setTimeout(async () => {
        const { street } = parseAddress(value)
        if (!street) return
        try {
          const { data } = await searchAddresses({ variables: { search: street.toUpperCase() } })
          const results = data?.addressAutoFills?.edges?.map(({ node }) => node.address) || []
          setAddressOptions(results)
        } catch {
          setAddressOptions([])
        }
      }, 300)
    },
    [searchAddresses]
  )

  const handleCitySearch = useCallback(
    (value) => {
      if (cityTimerRef.current) clearTimeout(cityTimerRef.current)
      if (!value || value.length < 2) {
        setCityOptions([])
        return
      }
      cityTimerRef.current = setTimeout(async () => {
        const { data } = await searchCities({ variables: { search: value.toUpperCase() } })
        const results = data?.cityAutoFills?.edges?.map(({ node }) => node.city) || []
        setCityOptions(results)
      }, 300)
    },
    [searchCities]
  )

  return (
    <div>
      <label className="label-checkbox">
        <input
          type="checkbox"
          checked={isDepotState}
          disabled={isNotModifiable}
          onChange={onChangeDepot}
        />
        {t('bdnDepot')}
      </label>
      {isDepotState ? (
        <TextField
          label={label}
          select
          fullWidth
          value={idState || ''}
          onChange={onSiteChange}
          margin="normal"
          disabled={isNotModifiable}
        >
          {availableSitesBdn.map((site) => (
            <MenuItem key={`${site.id}-${site.code}`} value={site.id}>
              {`${site.code} - ${site.address}`}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <>
          <Autocomplete
            className="little-margin-top"
            options={availableSites}
            value={id ? { code: codeState } : null}
            getOptionLabel={(option) => (option ? option.code : codeState)}
            onChange={handleSiteCodeChange}
            onInputChange={handleSiteCodeInputChange}
            renderInput={(params) => <AutocompleteTextField {...params} label={t('siteCode')} />}
            clearIcon={null}
            freeSolo
            disabled={isNotModifiable}
            sx={{
              '& svg': { position: 'static' },
            }}
          />
          <Autocomplete
            options={addressOptions}
            value={addressState || ''}
            getOptionLabel={(option) => option || ''}
            filterOptions={customFilterOptions}
            onChange={(event, newValue) => {
              if (newValue) {
                const { number: inputNumber } = parseAddress(addressState || '')
                const finalValue = inputNumber ? `${inputNumber} ${newValue}` : newValue
                const mockEvent = { target: { value: finalValue } }
                onAddressChange(mockEvent)
              } else {
                const mockEvent = { target: { value: '' } }
                onAddressChange(mockEvent)
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset') return
              const mockEvent = { target: { value: newInputValue } }
              onAddressChange(mockEvent)
              handleAddressSearch(newInputValue)
            }}
            renderOption={(props, option, { inputValue }) => (
              <li {...props}>{formatOption(option, inputValue)}</li>
            )}
            renderInput={(params) => (
              <AutocompleteTextField {...params} label={t('address')} margin="normal" />
            )}
            clearIcon={null}
            fullWidth
            disabled={isNotModifiable}
            sx={{
              '& svg': { position: 'static' },
            }}
          />
          <Autocomplete
            options={cityOptions}
            value={cityState || ''}
            getOptionLabel={(option) => option || ''}
            filterOptions={customFilterOptions}
            onChange={(event, newValue) => {
              if (newValue) {
                const { number: inputNumber } = parseAddress(cityState || '')
                const finalValue = inputNumber ? `${inputNumber} ${newValue}` : newValue
                const mockEvent = { target: { value: finalValue } }
                onCityChange(mockEvent)
              } else {
                const mockEvent = { target: { value: '' } }
                onCityChange(mockEvent)
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset') return
              const mockEvent = { target: { value: newInputValue } }
              onCityChange(mockEvent)
              handleCitySearch(newInputValue)
            }}
            renderInput={(params) => (
              <AutocompleteTextField {...params} label={t('city')} margin="normal" />
            )}
            clearIcon={null}
            fullWidth
            disabled={isNotModifiable}
            sx={{
              '& svg': { position: 'static' },
            }}
          />
        </>
      )}
    </div>
  )
}

CheckboxSelectSite.propTypes = {
  availableSites: PropTypes.any,
  availableSitesBdn: PropTypes.any,
  label: PropTypes.string,
  siteKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  orderDelivery: PropTypes.any,
  isNotModifiable: PropTypes.bool,
}

export default CheckboxSelectSite
